import React, { useState, useEffect } from 'react';
import { getRoles, deleteRole } from '../services/api/roleApi';
import RolePermissions from './RolePermissions';
import RoleForm from './RoleForm';
import RoleUsers from './RoleUsers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faShieldAlt, faTrash, faUsers } from '@fortawesome/free-solid-svg-icons';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list');
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await getRoles();
      setRoles(response.data);
    } catch (err) {
      setError('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
  };

  const confirmDeleteRole = async () => {
    try {
      await deleteRole(roleToDelete.id);
      setRoles(roles.filter(role => role.id !== roleToDelete.id));
      setRoleToDelete(null);
    } catch (err) {
      setError('Failed to delete role');
    }
  };

  const cancelDeleteRole = () => {
    setRoleToDelete(null);
  };
  
  const handleEditRole = (role) => {
    setSelectedRole(role);
    setView('edit');
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setView('create');
  };

  const handleManagePermissions = (role) => {
    setSelectedRole(role);
    setView('permissions');
  };
  
  const handleManageUsers = (role) => {
    setSelectedRole(role);
    setView('users');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedRole(null);
    fetchRoles(); // Refresh the list
  };

  if (loading && view === 'list') return <div>Loading roles...</div>;
  if (error && view === 'list') return <div className="error">{error}</div>;

  const renderContent = () => {
    switch (view) {
      case 'edit':
      case 'create':
        return (
          <RoleForm 
            role={selectedRole} 
            onSave={handleBackToList} 
            onCancel={handleBackToList} 
          />
        );
      case 'permissions':
        return (
          <RolePermissions 
            role={selectedRole} 
            onBack={handleBackToList} 
          />
        );
      case 'users':
        return (
          <RoleUsers
            role={selectedRole}
            onBack={handleBackToList}
          />
        );
      case 'list':
      default:
        return (
          <div className="user-list">
            <div className="user-list-header">
              <h2>Roles</h2>
              <button onClick={handleCreateRole} className="btn btn-primary">
                Create Role
              </button>
            </div>

            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map(role => (
                  <tr key={role.id}>
                    <td>{role.name}</td>
                    <td>{role.description}</td>
                    <td>{role.permissionCount || 0} {(role.permissionCount || 0) === 1 ? 'permission' : 'permissions'}</td>
                    <td>
                      <button 
                        onClick={() => handleEditRole(role)} 
                        className="btn btn-outline-secondary btn-sm mx-1"
                        title="Edit Role"
                      >
                        <FontAwesomeIcon icon={faEdit} aria-hidden="true" /> <span className="sr-only">Edit</span>
                      </button>
                      <button 
                        onClick={() => handleManagePermissions(role)} 
                        className="btn btn-outline-info btn-sm mx-1"
                        title="Manage Permissions"
                      >
                        <FontAwesomeIcon icon={faShieldAlt} aria-hidden="true" /> <span className="sr-only">Permissions</span>
                      </button>
                      <button 
                        onClick={() => handleManageUsers(role)} 
                        className="btn btn-outline-primary btn-sm mx-1"
                        title="Manage Users"
                      >
                        <FontAwesomeIcon icon={faUsers} aria-hidden="true" /> <span className="sr-only">Users</span>
                      </button>
                      <button 
                        onClick={() => handleDeleteRole(role)} 
                        className="btn btn-outline-danger btn-sm mx-1"
                        title="Delete Role"
                      >
                        <FontAwesomeIcon icon={faTrash} aria-hidden="true" /> <span className="sr-only">Delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  };

  return (
    <div>
      {renderContent()}
      
      {/* Delete Confirmation Modal */}
      {roleToDelete && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={cancelDeleteRole}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the role <strong>"{roleToDelete.name}"</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cancelDeleteRole}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDeleteRole}>
                  Delete Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {roleToDelete && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default RoleList;